import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

const SearchableMultiSelectMC = ({
  options = [],
  selected = [],
  onChange,
  onClearAll
}) => {
  const { t } = useTranslation('Common');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const textGetSelected = ((count) => t('searchable_multi_select.selected', { count }));

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    return options.filter(option =>
      option.label.toLowerCase().includes(term)
    );
  }, [options, searchTerm]);

  const selectedIds = useMemo(() =>
    new Set(selected.map(item => item.value)),
    [selected]
  );

  const handleToggleOption = (option) => {
    const isSelected = selectedIds.has(option.value);
    if (isSelected) {
      onChange(selected.filter(item => item.value !== option.value));
    } else {
      onChange([...selected, option]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
        setSearchTerm('');
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  return (
    <div className="tw-relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        className="tw-w-full tw-min-h-[48px] tw-px-4 tw-py-2 tw-text-left tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg tw-shadow-sm hover:tw-border-blue-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 tw-flex tw-items-center tw-justify-between"
      >
        <span className={selected.length === 0 ? 'tw-text-gray-400' : 'tw-text-gray-900'}>
          {selected.length === 0 ? t('searchable_multi_select.placeholder') : textGetSelected(selected.length)}
        </span>
        <svg
          className={`tw-w-5 tw-h-5 tw-text-gray-400 tw-transition-transform ${dropdownOpen ? 'tw-rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {dropdownOpen && (
        <div className="tw-absolute tw-z-50 tw-w-full tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-lg tw-max-h-96 tw-overflow-hidden tw-flex tw-flex-col">
          <div className="tw-p-3 tw-border-b tw-border-gray-200">
            <input
              type="text"
              placeholder={t('searchable_multi_select.search_placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500"
              autoFocus
            />
          </div>
          <div className="tw-overflow-y-auto tw-max-h-64">
            {filteredOptions.length === 0 ? (
              <div className="tw-p-4 tw-text-center tw-text-gray-500 tw-text-sm">
                {t('searchable_multi_select.no_options_text')}
              </div>
            ) : (
              <div className="tw-py-2">
                {filteredOptions.map((option) => {
                  const isSelected = selectedIds.has(option.value);
                  return (
                    <label
                      key={option.value}
                      className="tw-flex tw-items-center tw-px-4 tw-py-2 hover:tw-bg-gray-50 tw-cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleOption(option)}
                        className="tw-w-4 tw-h-4 tw-text-blue-600 tw-bg-gray-100 tw-border-gray-300 tw-rounded focus:tw-ring-blue-500 focus:tw-ring-2"
                      />
                      <span className="tw-ml-3 tw-text-sm tw-text-gray-900">
                        {option.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
          {selected.length > 0 && (
            <div className="tw-p-3 tw-border-t tw-border-gray-200 tw-bg-gray-50">
              <div className="tw-flex tw-items-center tw-justify-between">
                <span className="tw-text-sm tw-text-gray-700">
                  {textGetSelected(selected.length)}
                </span>
                {onClearAll && (
                  <button
                    type="button"
                    onClick={onClearAll}
                    className="tw-text-sm tw-text-blue-600 hover:tw-text-blue-800"
                  >
                    {t('searchable_multi_select.clear_all_text')}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

SearchableMultiSelectMC.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })
  ),
  selected: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
    })
  ),
  onChange: PropTypes.func.isRequired,
  onClearAll: PropTypes.func,
  placeholder: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  noOptionsText: PropTypes.string,
  clearAllText: PropTypes.string,
  getSelectedText: PropTypes.func
};

export default SearchableMultiSelectMC;
